import './index.css';
import { render } from 'preact';
import Router from 'preact-router';
import { useState } from 'preact/hooks';
import { ImpersonationBanner } from './components/ImpersonationBanner.js';
import { InstallBanner } from './components/InstallBanner.js';
import { UpdateToast } from './components/UpdateToast.js';
import { AuthGuard } from './components/ui/AuthGuard.js';
import { BottomNav } from './components/ui/BottomNav.js';
import { NotificationBell } from './components/ui/NotificationBell.js';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import { usePushRegistrationRepair } from './hooks/usePushRegistrationRepair.js';
import { useShellChromeHeight } from './hooks/useShellChromeHeight.js';
import { ImpersonateExchangePage } from './pages/ImpersonateExchangePage.js';
import { AccountPage } from './pages/account/AccountPage.js';
import { AuthCallbackPage } from './pages/account/AuthCallbackPage.js';
import { BookingsPage } from './pages/account/BookingsPage.js';
import { ForgotPasswordPage } from './pages/account/ForgotPasswordPage.js';
import { IdentityPage } from './pages/account/IdentityPage.js';
import { LoginPage } from './pages/account/LoginPage.js';
import { MyReviewsPage } from './pages/account/MyReviewsPage.js';
import { NotificationsPage } from './pages/account/NotificationsPage.js';
import { PenaltiesPage } from './pages/account/PenaltiesPage.js';
import { PenaltyPage } from './pages/account/PenaltyPage.js';
import { ProfileEditPage } from './pages/account/ProfileEditPage.js';
import { PushNotificationsSettings } from './pages/account/PushNotificationsSettings.js';
import { RegisterPage } from './pages/account/RegisterPage.js';
import { ResetPasswordPage } from './pages/account/ResetPasswordPage.js';
import { SecurityPage } from './pages/account/SecurityPage.js';
import { VerifyEmailPage } from './pages/account/VerifyEmailPage.js';
import { BookingConfirmPage } from './pages/booking/BookingConfirmPage.js';
import { BookingFormPage } from './pages/booking/BookingFormPage.js';
import { BookingReturnPage } from './pages/booking/BookingReturnPage.js';
import { FuelPhotoPage } from './pages/booking/FuelPhotoPage.js';
import { InspectionCapturePage } from './pages/booking/InspectionCapturePage.js';
import { CarDetailPage } from './pages/car/CarDetailPage.js';
import { CarReviewsPage } from './pages/car/CarReviewsPage.js';
import { HomePage } from './pages/home/HomePage.js';
import { HostLinkPage } from './pages/host/HostLinkPage.js';
import { PrivacyPolicyPage, TermsOfServicePage } from './pages/legal/LegalDocumentPage.js';
import { SearchResultsPage } from './pages/search/SearchResultsPage.js';
import { SupportChat } from './support/SupportChat.js';

/**
 * 078: the notification control, mounted in the shell.
 *
 * It lives here rather than in `PageHeader` because `PageHeader` is rendered by **4 of 26** page
 * files, all of them account settings screens — home, search, bookings and the whole booking flow
 * have no header, so a bell there would be invisible everywhere a renter actually is (research R5).
 *
 * Signed-in is decided from `AuthContext`, never by asking `/notifications` and reading a 401: a
 * public screen must make no request at all (FR-013). `loading` renders nothing, because "we do not
 * know yet" is not "signed out" — a bell that flickers in on every page load is its own defect.
 */
function ShellNotificationBell({ visible }: { visible: boolean }) {
  const { isAuthenticated, loading } = useAuth();
  if (!visible || loading || !isAuthenticated) return null;
  return (
    <div class="flex justify-end px-4 pt-3">
      <NotificationBell />
    </div>
  );
}

/**
 * 197 (research.md §1): mounted once here, alongside `ShellNotificationBell`, so it has the same
 * `AuthContext` access. Renders nothing either way — the hook itself gates on `isAuthenticated`
 * and `loading` (contracts/push-repair.md) and is silent on both outcomes (FR-018).
 */
function ShellPushRegistrationRepair() {
  usePushRegistrationRepair();
  return null;
}

export function App() {
  const [currentPath, setCurrentPath] = useState('/');
  // 139: the install offer reports its measured height here rather than publishing a property of
  // its own, so `--shell-chrome-h` stays the single figure every screen subtracts (FR-005).
  const [offerPx, setOfferPx] = useState(0);

  function handleRoute(e: { url: string }) {
    setCurrentPath(e.url);
  }

  const showNav =
    !currentPath.startsWith('/account/login') &&
    !currentPath.startsWith('/account/register') &&
    !currentPath.startsWith('/account/forgot-password') &&
    !currentPath.startsWith('/account/reset-password') &&
    !currentPath.startsWith('/account/verify-email') &&
    !currentPath.startsWith('/auth/callback') &&
    !currentPath.startsWith('/account/security') &&
    // 138 follow-up: the impersonation hand-off exchanges a code and routes away within a tick —
    // there is nothing to navigate to from it, and the bell it was reserving room for cannot
    // render, because the session it needs is the one this screen is still acquiring. Leaving it
    // in `showNav` forced the screen to subtract a 132px chrome reserve that was only ever 80px
    // present, and its message measured 66px above centre live. It stays OUTSIDE `AuthGuard`,
    // which is a different thing and deliberately unchanged.
    !currentPath.startsWith('/impersonate');

  // 139: measured, not stated. The `132px` this replaces was wrong on every route where the bell
  // does not render — see `shell/chromeHeight.ts`.
  const { noticesRef, bellRef } = useShellChromeHeight({ navVisible: showNav, offerPx });

  return (
    <AuthProvider>
      <div class="bg-bg min-h-dvh text-text">
        {/* 129: shared sticky slot for every global banner — `ImpersonationBanner` and
            `UpdateToast` stack as plain flow children here rather than each carrying its own
            `sticky top-0`, which collided once scrolled (two siblings both pinned to the same
            `top: 0`). This wrapper is correct regardless of either banner's height. */}
        <div
          ref={noticesRef}
          data-testid="global-banners"
          class="sticky top-0 z-[100] flex flex-col"
        >
          <ImpersonationBanner />
          <UpdateToast />
        </div>
        {/* Above the routed content and outside it, so the panel cannot be clipped by a screen's
            own overflow and the control cannot overlap the fixed bottom navigation. */}
        {/* 139: wrapped so its height can be measured. `ShellNotificationBell` renders `null`
            when signed out, and a ref cannot attach to nothing — this wrapper contributes no
            height of its own in that case, which is exactly the 52px the old constant wrongly
            reserved. */}
        <div ref={bellRef}>
          <ShellNotificationBell visible={showNav} />
        </div>
        <ShellPushRegistrationRepair />
        {/* 378 (#1266, trial): LiveAgent's chat button on every page; nothing unless configured. */}
        <SupportChat aboveNav={showNav} />
        <div data-testid="app-content" class={showNav ? 'pb-20' : ''}>
          <Router onChange={handleRoute}>
            <ImpersonateExchangePage path="/impersonate" />
            <AuthGuard path="/">
              <HomePage />
            </AuthGuard>
            {/* 343 (#937): a host's share link — public, so it keeps the host before any login. */}
            <HostLinkPage path="/h/:name" />
            <AuthGuard path="/search">
              <SearchResultsPage />
            </AuthGuard>
            <AuthGuard path="/cars/:id">
              <CarDetailPage />
            </AuthGuard>
            {/* 363 (#1090): every review of a car, from "See all N reviews". */}
            <AuthGuard path="/cars/:id/reviews">
              <CarReviewsPage />
            </AuthGuard>
            <AuthGuard path="/booking/:id">
              <BookingFormPage />
            </AuthGuard>
            <AuthGuard path="/booking/:id/confirm">
              <BookingConfirmPage />
            </AuthGuard>
            <AuthGuard path="/booking/:id/return">
              <BookingReturnPage />
            </AuthGuard>
            {/* 210 (#306): :type is 'checkout' | 'checkin' — the screen itself decides which,
                falling back to 'checkout' for anything else (defensive, not a route it should
                ever actually receive). */}
            <AuthGuard path="/booking/:id/inspection/:type">
              <InspectionCapturePage />
            </AuthGuard>
            <AuthGuard path="/booking/:id/fuel">
              <FuelPhotoPage />
            </AuthGuard>
            <AuthGuard path="/account">
              <AccountPage />
            </AuthGuard>
            <AuthGuard path="/account/bookings">
              <BookingsPage />
            </AuthGuard>
            <AuthGuard path="/account/profile">
              <ProfileEditPage />
            </AuthGuard>
            <AuthGuard path="/account/reviews">
              <MyReviewsPage />
            </AuthGuard>
            <AuthGuard path="/account/identity">
              <IdentityPage />
            </AuthGuard>
            {/* 393 (#1313): the customer's penalties, and one penalty (pay by QR, upload the receipt). */}
            <AuthGuard path="/account/penalties">
              <PenaltiesPage />
            </AuthGuard>
            <AuthGuard path="/account/penalties/:id">
              <PenaltyPage />
            </AuthGuard>
            <LoginPage path="/account/login" />
            <RegisterPage path="/account/register" />
            <AuthCallbackPage path="/auth/callback" />
            {/* 084 (gap-list F7): the list takes this address and the push toggle moves under
                it. The menu already said "Notifications" and led to a push toggle, so adding a
                list is what made that label wrong — the rename is caused by this feature.
                **Bookmarks to the old address break, and that is accepted** (spec Assumption 2).

                Listed parent-first, mirroring `/booking/:id` above: preact-router matches on equal
                segment counts, so a parent route does not swallow its children. */}
            <AuthGuard path="/account/notifications">
              <NotificationsPage />
            </AuthGuard>
            <AuthGuard path="/account/notifications/push">
              <PushNotificationsSettings />
            </AuthGuard>
            <AuthGuard path="/account/security">
              <SecurityPage path="/account/security" />
            </AuthGuard>
            {/* 153 (#23): public, and deliberately OUTSIDE AuthGuard — terms a customer is
                asked to agree to must be readable without an account. */}
            {/* 160: public legal pages, outside AuthGuard. `/terms` is kept as an alias because
                feature 153 shipped that address into the booking form and into links customers may
                already hold — it must not start 404ing (US2 AC3). */}
            <TermsOfServicePage path="/terms-of-service" />
            <TermsOfServicePage path="/terms" />
            <PrivacyPolicyPage path="/privacy-policy" />
            <ForgotPasswordPage path="/account/forgot-password" />
            <ResetPasswordPage path="/account/reset-password" />
            <VerifyEmailPage path="/account/verify-email" />
          </Router>
        </div>
        {/* 138: mounted here, NOT inside `global-banners`. That slot is the sticky top column, and
            everything in it adds its own height to the page below — 40px measured live, enough to
            give a screen that otherwise fits a band of empty scroll room (the defect 134/136/137
            removed). This one is `fixed` to the bottom edge and costs the page nothing. It takes
            `showNav` rather than re-deriving the route rules, so it clears the tab bar exactly
            where the tab bar exists. */}
        <InstallBanner navVisible={showNav} onHeightChange={setOfferPx} />
        {showNav && <BottomNav currentPath={currentPath} />}
      </div>
    </AuthProvider>
  );
}

const rootEl = document.getElementById('app');
if (rootEl) {
  render(<App />, rootEl);
}
